import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  IconDeviceFloppy,
  IconRefreshDot,
  IconSettingsCode,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  getResellerOptions,
  updateResellerOptions,
} from "../../../../api/ResellerAPI";
import { notifications } from "@mantine/notifications";
import { getPermission } from "../../../../functions/utilFunctions";

const ResellersOptions = () => {
  const lang = useLanguage((s) => s.language);
  const { resellerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [updating, setUpdating] = useState(false);

  const [chatwootActive, setChatwootActive] = useState(false);
  const [newUserPermissionsActive, setNewUserPermissionsActive] = useState(false);
  const [smsRegister, setSmsRegister] = useState(false);

  const names = useMemo(() => {
    let result = [
      {
        key: "SubscriptionFreeTrial",
        type: "boolean",
        label: lang.resellers.options.SubscriptionFreeTrial,
      },
      {
        key: "ShowSmtpConnectionGmail",
        type: "boolean",
        label: lang.resellers.options.ShowSmtpConnectionGmail,
      },
      {
        key: "ShowSmtpConnectionOutlook",
        type: "boolean",
        label: lang.resellers.options.ShowSmtpConnectionOutlook,
      },
      {
        key: "ShowRegisterConnectionGmail",
        type: "boolean",
        label: lang.resellers.options.ShowRegisterConnectionGmail,
      },
      {
        key: "ShowRegisterConnectionOutlook",
        type: "boolean",
        label: lang.resellers.options.ShowRegisterConnectionOutlook,
      },
      {
        key: "SmsVerificationOnRegister",
        type: "variables",
        label: lang.resellers.options.SmsVerificationOnRegister,
        activeState: smsRegister,
        setActiveState: setSmsRegister,
        variables: [
          {
            key: "SENDER",
            type: "text",
            label: lang.resellers.options.SmsVerificationOnRegister_sender,
            placeholder: lang.components.email_preview.sender,
            inputProps: {
              pattern: "^(?=.*[A-Za-z])[A-Za-z0-9]{4,11}$",
              title: lang.resellers.options.SmsVerificationOnRegister_pattern,
            },
          },
        ],
      },
      {
        key: "Chatwoot",
        type: "variables",
        label: "Chatwoot",
        activeState: chatwootActive,
        setActiveState: setChatwootActive,
        variables: [
          {
            key: "BASE_URL",
            type: "url",
            label: "BASE_URL",
            placeholder: "https://app.chatwoot.com",
          },
          {
            key: "WEBSITE_TOKEN",
            type: "text",
            label: "WEBSITE_TOKEN",
            placeholder: "XXXXXXXXXXXXXXXXXXXXXXXX",
          },
        ],
      },
    ];

    if (getPermission("resellerSetDefaultUserPermissions")) {
      result.push({
        key: "NewUserPermissions",
        type: "variables",
        label: "Droits des nouveaux utilisateurs",
        activeState: newUserPermissionsActive,
        setActiveState: setNewUserPermissionsActive,
        variables: [
          {
            key: "maxIntegrationsEmailSMTP",
            type: "number",
            label: lang.administration.right.limits.list.maxIntegrationsEmailSMTP,
          },
          {
            key: "maxIntegrationsLinkedin",
            type: "number",
            label: lang.administration.right.limits.list.maxIntegrationsLinkedin + " LinkedIn",
          },
          {
            key: "maxEmailsGeneratedPerMonth",
            type: "number",
            label: lang.administration.right.limits.list.maxEmailsGeneratedPerMonth,
          },
        ],
      });
    }

    return result
  }, [smsRegister, chatwootActive, newUserPermissionsActive, lang]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    setUpdating(true);
    updateResellerOptions({
      reseller_id: resellerId,
      options: names.map((item) => {
        let resultVariables = null;

        if (item?.variables) {
          resultVariables = {};

          item.variables.forEach((varItem) => {
            resultVariables[varItem.key] = varItem.type === "number" ? parseInt(data[`${item.key}-${varItem.key}`]) || 0 : data[`${item.key}-${varItem.key}`];
          });
        }

        return {
          name: item.key,
          active: data?.[item.key] === "on" ? true : false,
          variables: resultVariables,
        };
      }),
    })
      .then((r) => {
        notifications.show({
          message: lang.resellers.options.saved,
          color: "green.8",
        });
      })
      .finally(() => setUpdating(false));
  };

  useEffect(() => {
    if (loading) {
      getResellerOptions(resellerId)
        .then((r) => {
          let result = r.data.options;
          setOptions(result);
          setChatwootActive(result.find((x) => x.name === "Chatwoot")?.active);
          setNewUserPermissionsActive(result.find((x) => x.name === "NewUserPermissions")?.active);
          setSmsRegister(
            result.find((x) => x.name === "SmsVerificationOnRegister")?.active
          );
        })
        .finally(() => setLoading(false));
    }
  }, [loading]);

  return (
    <Accordion variant="filled" mt={"md"} radius={"md"}>
      <Accordion.Item value="advanced" bg={"#fff"} radius={"md"} px={"lg"}>
        <Accordion.Control bg={"#fff"}>
          <Group>
            <IconSettingsCode />
            <Text size="xl" fw={700}>
              {lang.resellers.options.title}
            </Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {loading ? (
            <Stack pos={"relative"} h={200}>
              <LoadingOverlay visible />
            </Stack>
          ) : (
            <Stack component={"form"} onSubmit={handleSubmit} pos={"relative"}>
              <LoadingOverlay visible={updating} />

              {names.map((item, index) => {
                return (
                  <>
                    <Checkbox
                      key={item.key}
                      size="lg"
                      defaultChecked={
                        options?.find?.((x) => x?.name === item.key)?.active
                      }
                      onChange={
                        item?.type === "boolean"
                          ? undefined
                          : (e) => item.setActiveState(e.target.checked)
                      }
                      name={item.key}
                      label={item.label}
                    />

                    {item?.variables && (
                      <Stack
                        ml={15}
                        pl={25}
                        style={{ borderLeft: "1px solid #00000021" }}
                      >
                        {item.variables.map((varItem) => (
                          varItem.type === "number" ?
                            <NumberInput
                              key={varItem.key}
                              name={`${item.key}-${varItem.key}`}
                              label={varItem.label}
                              placeholder={varItem.placeholder}
                              defaultValue={
                                options?.find?.((x) => x?.name === item.key)
                                  ?.variables[varItem.key]
                              }
                              disabled={!item.activeState}
                              required={item.activeState}
                              size="md"
                              w={300}
                              {...(varItem?.inputProps || {})} />
                            :
                            <TextInput
                              key={varItem.key}
                              name={`${item.key}-${varItem.key}`}
                              label={varItem.label}
                              type={varItem.type}
                              placeholder={varItem.placeholder}
                              defaultValue={
                                options?.find?.((x) => x?.name === item.key)
                                  ?.variables[varItem.key]
                              }
                              disabled={!item.activeState}
                              required={item.activeState}
                              size="md"
                              w={300}
                              {...(varItem?.inputProps || {})}
                            />
                        ))}
                      </Stack>
                    )}

                    {index < names.length - 1 && <Divider my={4} />}
                  </>
                );
              })}

              <Group justify="center" mt={"lg"} gap={"xl"}>
                <Button
                  type="reset"
                  variant="light"
                  leftSection={<IconRefreshDot />}
                  size="md"
                >
                  {lang.prm.toolbar.reset}
                </Button>
                <Button
                  type="submit"
                  leftSection={<IconDeviceFloppy />}
                  size="md"
                >
                  {lang.global.save}
                </Button>
              </Group>
            </Stack>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default ResellersOptions;
